import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { ReactNode } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { ValidKycLevelType } from '@flipster/apis/v1/kyc/types';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { getLevelByNumber, useKYC } from '@flipster/features/src/shared/kyc';
import { useZendeskEmergency } from '@flipster/features/src/shared/zendesk/use-zendesk-emergency';
import { usePathname } from '@flipster/i18n/routing';
import { useOverlay } from '@flipster/overlay';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import CautionCircleIcon from '@flipster/react/icon/generated/CautionCircleIcon';
import InProgressIcon from '@flipster/react/icon/generated/InProgressIcon';
import XMarkIcon from '@flipster/react/icon/generated/XMarkIcon';
import { useMounted } from '@flipster/react/misc/lifecycle/mount';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { GnbHeightVariableKey } from '@flipster/react/panda/globals/vars';
import { css } from '@flipster/styled-system/css';
import { center, hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import segment, { KycEvent } from '@flipster/utils/segment';

import { KycModal } from '@web/app/_kyc-modal';

export const AlertBanner = () => {
  const transAssets = useTranslations('Assets.r250401');
  const transKyc = useTranslations('Kyc.r250804');
  const transCommon = useTranslations('Common.r250804');
  const pathname = usePathname();

  const { isLoggedInAtom, getMeAtom } = useBunja(authBunja);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const { isLoading } = useAtomValue(getMeAtom);

  const isMounted = useMounted();
  const overlay = useOverlay();
  const { isDesktop } = useBreakpoint();
  const { warningStatus, kycInfo, nextLevel } = useKYC();
  const { zendeskEmergency, closeEmergency } = useZendeskEmergency();

  const isHideAlertBanner = ['/trade', '/user/account', '/user/kyc'].some((p) => pathname.startsWith(p));

  if (!isMounted || isLoading || isHideAlertBanner) return null;

  // kyc alert
  if (isLoggedIn && kycInfo) {
    const kycAlertBanner = {
      RequireNextLevel: {
        icon: <CautionCircleIcon color={token('colors.content.state.warning')} />,
        text: transAssets('Alert_verification_level_required_description', {
          level: getLevelByNumber(kycInfo.desirableKYCLevel),
        }),
        actionText: transAssets('Verify_now'),
        onClick: () => overlay.open(() => <KycModal level={nextLevel as ValidKycLevelType} />),
      },
      InProgress: {
        icon: <InProgressIcon color={token('colors.content.accent.info')} />,
        text: transAssets('Alert_verification_review_in_progress_description'),
        actionText: transAssets('View_status'),
        linkPath: '/user/kyc',
      },
      Resubmit: {
        icon: <CautionCircleIcon color={token('colors.content.state.warning')} />,
        text: transAssets('Alert_please_resubmit_description'),
        actionText: transAssets('Verify_now'),
        linkPath: '/user/kyc',
      },
      TncAgreementRequired: {
        icon: <CautionCircleIcon color={token('colors.content.state.negative')} />,
        text: transKyc('Alert_accept_terms_to_continue_description'),
        actionText: transCommon('Accept_now'),
        linkPath: '/user/kyc',
      },
    }[warningStatus as string];

    if (kycAlertBanner) {
      const trackingSegment = () => {
        segment.track(KycEvent.KycVerificationBannerClick, {
          kyc_level: getLevelByNumber(kycInfo.currentKYCLevel),
          banner_text: kycAlertBanner.text,
          link_text: kycAlertBanner.actionText,
        });
      };

      return (
        <AlertBannerLayout
          icon={kycAlertBanner.icon}
          text={kycAlertBanner.text}
          action={
            kycAlertBanner.linkPath ? (
              <Link href={kycAlertBanner.linkPath} onClick={trackingSegment} className={actionCss}>
                {kycAlertBanner.actionText}
              </Link>
            ) : (
              <button
                type='button'
                onClick={() => {
                  kycAlertBanner.onClick?.();
                  trackingSegment();
                }}
                className={actionCss}
              >
                {kycAlertBanner.actionText}
              </button>
            )
          }
        />
      );
    }
  }
  // emergency alert
  if (zendeskEmergency?.title) {
    const MaxLength = 56;
    let text = zendeskEmergency.title;
    const isEllipsis = text.length > MaxLength;
    if (!isDesktop && isEllipsis) text = text.slice(0, MaxLength).trim() + '...';
    const onClose = () => closeEmergency(zendeskEmergency.id);

    return (
      <AlertBannerLayout
        icon={<CautionCircleIcon color={token('colors.content.state.warning')} />}
        text={text}
        action={
          <IsomorphicLink href={zendeskEmergency.html_url} newTab onClick={onClose} className={actionCss}>
            Learn more
          </IsomorphicLink>
        }
        onClose={onClose}
      />
    );
  }

  return null;
};

interface TemplateProps {
  text: string;
  icon?: ReactNode;
  action?: ReactNode;
  onClose?: () => void;
}

const AlertBannerLayout = ({ icon, text, action, onClose }: TemplateProps) => {
  const { isMobile } = useBreakpoint();

  useIsomorphicLayoutEffect(() => {
    document.documentElement.classList.add('alert-banner');
    return () => document.documentElement.classList.remove('alert-banner');
  }, [isMobile]);

  return (
    <>
      <div
        className={css({
          w: '100%',
          h: { base: 'alertBannerMobileHeight', tablet: 'alertBannerAfterMobileHeight' },
        })}
      />
      <div
        className={css({
          zIndex: 'alertBanner',
          pos: 'fixed',
          left: 0,
          w: '100%',
          h: { base: 'alertBannerMobileHeight', tablet: 'alertBannerAfterMobileHeight' },
          bgColor: 'background.default.level-1',
        })}
        style={{ top: `var(${GnbHeightVariableKey})` }}
      >
        <div
          className={hstack({
            pos: 'relative',
            alignItems: { base: 'flex-start', tablet: 'center' },
            justify: { base: 'flex-start', tablet: 'center' },
            gap: 8,
            py: 12,
            px: { base: 16, tablet: 24 },
          })}
        >
          {icon && <span className={center({ '& > svg': { w: 20, h: 20 } })}>{icon}</span>}
          <div
            className={css({
              textStyle: 'Text/M/Regular',
              color: 'content.default.level-4',
              tabletDown: { flex: 1 },
            })}
          >
            {text}
            {action}
          </div>
          {onClose && (
            <button
              type='button'
              onClick={onClose}
              className={css({ buttonShape: 'inline-icon', tablet: { pos: 'absolute', right: 24 } })}
              aria-label='Close'
            >
              <XMarkIcon size={20} color={token('colors.content.default.level-3')} />
            </button>
          )}
        </div>
      </div>
    </>
  );
};

const actionCss = css({ textStyle: 'Text/L/Underline', color: 'content.accent.info', ml: 8 });
